import React from 'react';
import type { Nullable } from 'corgi-types';

import {
  getMediaWidthMax,
  getMediaWidthMin,
  getMediaWidthMinMax,
} from '../utils/responsive-image/media-condition';

enum ImageSetType {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop',
  LargeDesktop = 'large_desktop',
}

export interface ImageSet {
  x1: Nullable<string>;
  x2: Nullable<string>;
  x3: Nullable<string>;
}

export interface PrismicImage {
  mobile: Nullable<ImageSet>;
  tablet: Nullable<ImageSet>;
  desktop: Nullable<ImageSet>;
  large_desktop?: Nullable<ImageSet>;
  alt?: string;
}

interface PrismicResponsiveImageProps {
  image: PrismicImage;
  highPriority?: true;
  className?: string;
}

const MEDIA_QUERIES_ALL = new Map([
  [ImageSetType.Mobile, getMediaWidthMax(0)],
  [ImageSetType.Tablet, getMediaWidthMinMax(1)],
  [ImageSetType.Desktop, getMediaWidthMinMax(2)],
  [ImageSetType.LargeDesktop, getMediaWidthMin(2)],
]);

const MEDIA_QUERIES_WITHOUT_LARGE = new Map([...MEDIA_QUERIES_ALL].slice(0, -1)).set(
  ImageSetType.Desktop,
  getMediaWidthMin(1)
);

const hasRequiredImageProps = (image: PrismicImage) =>
  image.mobile.x1 && image.tablet.x1 && image.desktop.x1;

const getSrcSet = ({ x1, x2, x3 }: ImageSet) =>
  `${x1} 1x${x2 ? `, ${x2} 2x` : ''}${x3 ? `, ${x3} 3x` : ''}`;

const getSources = (image: PrismicImage) => {
  const mediaQueries = image.large_desktop?.x1 ? MEDIA_QUERIES_ALL : MEDIA_QUERIES_WITHOUT_LARGE;

  return Object.values(ImageSetType)
    .filter(type => image[type])
    .map(type => ({
      key: type,
      media: mediaQueries.get(type),
      srcSet: getSrcSet(image[type]),
    }));
};

const PrismicResponsiveImage = ({
  image,
  highPriority,
  className,
}: PrismicResponsiveImageProps) => {
  if (!hasRequiredImageProps(image)) {
    // eslint-disable-next-line no-console
    console.warn('<PrismicResponsiveImage /> is missing required props');

    return null;
  }

  return (
    <picture>
      {getSources(image).map(({ key, media, srcSet }) => (
        <source key={key} media={media} srcSet={srcSet} />
      ))}
      <img
        src={image[ImageSetType.Mobile].x1}
        alt={image.alt ?? ''}
        className={className}
        {...(highPriority ? { fetchPriority: 'high' } : {})}
      />
    </picture>
  );
};

export default PrismicResponsiveImage;
